class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedBestSellerResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.bestAbortController = new AbortController();
    this.searchTerm = '';
    this._productsWheelBoundTo = null;
    this._productsWheelHandler = null;
    this._skippHeaderLayoutBound = false;

    this.setupEventListeners();
    this.setupSkippHeaderDropdownLayoutListeners();
  }

  setupSkippHeaderDropdownLayoutListeners() {
    if (this._skippHeaderLayoutBound) return;
    this._skippHeaderLayoutBound = true;
    const onLayout = () => {
      if (this.hasAttribute('open')) this.adjustSkippHeaderDropdownPosition();
    };
    window.addEventListener('resize', onLayout);
    window.addEventListener(
      'scroll',
      () => {
        if (this.hasAttribute('open')) this.adjustSkippHeaderDropdownPosition();
      },
      { passive: true }
    );
  }

  /**
   * mWeb: header results use position:fixed (see skipp-predictive-search.css). Anchor top to the field.
   */
  adjustSkippHeaderDropdownPosition() {
    const results = this.predictiveSearchResults;
    if (!results || !results.classList.contains('skipp-header-search__results')) return;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) {
      results.style.top = '';
      results.style.removeProperty('max-height');
      return;
    }

    const anchor = this.querySelector('.skipp-header-search__field') || this.input;
    if (!anchor) return;

    const topPx = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom + 6));
    results.style.top = `${topPx}px`;

    const avail = Math.max(160, window.innerHeight - topPx - 16);
    const mh = Math.min(400, avail);
    results.style.setProperty('max-height', `${mh}px`, 'important');
  }

  normalizeTerm(term) {
    return (term || '').toLowerCase().trim();
  }

  normalizeSearchText(text) {
    // Keep matching case-insensitive and also normalize common synonyms.
    // Example: "trousers" and "pants" should behave like the same query.
    return this.normalizeTerm(text)
      .replace(/\btrousers?\b/g, 'pant')
      .replace(/\bpants?\b/g, 'pant');
  }

  termTokens(term) {
    return this.normalizeSearchText(term).split(/\s+/g).filter(Boolean);
  }

  textMatchesTokens(text, tokens) {
    const t = this.normalizeSearchText(text);
    if (!tokens.length) return true;
    return tokens.every((tok) => {
      if (t.includes(tok)) return true;
      if (tok.length > 3 && tok.endsWith('s')) {
        const singular = tok.slice(0, -1);
        if (singular && t.includes(singular)) return true;
      }
      return false;
    });
  }

  scoreTitleMatch(title, term) {
    const t = this.normalizeSearchText(title);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (t === q) return 1000;
    if (t.startsWith(q)) return 900;
    if (t.includes(` ${q}`)) return 850;
    if (t.includes(q)) return 800;
    const tokens = q.split(/\s+/g).filter(Boolean);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    // Encourage multi-token matches, but keep it modest.
    return hits * 50;
  }

  scoreFieldMatch(text, term, { exact, startsWith, wordStart, contains, tokenHit }) {
    const t = this.normalizeSearchText(text);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (!t) return 0;
    if (t === q) return exact;
    if (t.startsWith(q)) return startsWith;
    if (t.includes(` ${q}`)) return wordStart;
    if (t.includes(q)) return contains;
    const tokens = this.termTokens(q);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    return hits * tokenHit;
  }

  isLuxeFromTags(tags) {
    const v = this.normalizeTerm(tags);
    return v.includes('luxe') || v.includes('luxury') || v.includes('skipp-luxe') || v.includes('skipp_luxe');
  }

  normalizeVendorKey(vendor) {
    // Normalize punctuation/ampersands so matching is robust.
    return this.normalizeTerm(vendor)
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  isExclusiveVendor(vendorRaw) {
    const key = this.normalizeVendorKey(vendorRaw);
    return key === 'lords and kings' || key === 'street junkies' || key === 'cop underdog';
  }

  getProductMetaFromLi(li) {
    return {
      title: li?.getAttribute?.('data-title') || li?.textContent || '',
      type: li?.getAttribute?.('data-type') || '',
      description: li?.getAttribute?.('data-description') || '',
      tags: li?.getAttribute?.('data-tags') || '',
      vendor: li?.getAttribute?.('data-vendor') || '',
    };
  }

  isHeaderInlineSearch() {
    return !!this.predictiveSearchResults?.classList?.contains('skipp-header-search__results');
  }

  /**
   * Empty query: show trending-only panel (same links as Liquid) without calling the predictive API.
   */
  renderTrendingBootstrap() {
    if (!this.isHeaderInlineSearch() || !this.predictiveSearchResults) return false;

    const script = document.getElementById('skipp-predictive-trending-bootstrap');
    if (!script) return false;

    let items;
    try {
      items = JSON.parse(script.textContent.trim());
    } catch (e) {
      return false;
    }
    if (!Array.isArray(items) || !items.length) return false;

    const searchUrl = typeof routes !== 'undefined' && routes.search_url ? routes.search_url : '/search';
    const esc = (s) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const links = items
      .map((item) => {
        const label = item.label || item.q || '';
        const q = item.q || label;
        const href = `${searchUrl}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
        return `<a href="${esc(href)}" class="skipp-predictive-trending__link link link--text" tabindex="-1">${esc(label)}</a>`;
      })
      .join('');

    const html = `
<div id="predictive-search-results" class="skipp-predictive-dropdown-root skipp-predictive-dropdown-root--bootstrap" role="listbox">
  <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--collections-and-products skipp-predictive-groups">
    <div class="skipp-predictive-section skipp-predictive-section--trending" role="group" aria-label="Trending searches">
      <p class="skipp-predictive-section__label" id="skipp-predictive-trending-heading">Trending searches</p>
      <div class="skipp-predictive-trending-list" aria-labelledby="skipp-predictive-trending-heading">${links}</div>
    </div>
  </div>
  <span class="hidden" data-predictive-search-live-region-count-value>Trending searches</span>
</div>`;

    this.predictiveSearchResults.innerHTML = html;
    this.searchTerm = '';
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.setLiveRegionText('Trending searches');
    this.open();
    return true;
  }

  stripQueryHighlights(root) {
    if (!root) return;
    root.querySelectorAll('mark.skipp-predictive-highlight').forEach((m) => {
      const p = m.parentNode;
      if (!p) return;
      p.replaceChild(document.createTextNode(m.textContent), m);
      p.normalize();
    });
  }

  highlightTextInElement(el, rawQuery) {
    if (!el || !rawQuery) return;
    const q = String(rawQuery).trim();
    if (!q) return;
    const needle = q.toLowerCase();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent || !node.parentElement) continue;
      if (node.parentElement.closest('mark.skipp-predictive-highlight')) continue;
      const txt = node.textContent;
      const idx = txt.toLowerCase().indexOf(needle);
      if (idx === -1) continue;
      const before = txt.slice(0, idx);
      const mid = txt.slice(idx, idx + q.length);
      const after = txt.slice(idx + q.length);
      const frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      const mark = document.createElement('mark');
      mark.className = 'skipp-predictive-highlight';
      mark.textContent = mid;
      frag.appendChild(mark);
      if (after) frag.appendChild(document.createTextNode(after));
      node.parentNode.replaceChild(frag, node);
      break;
    }
  }

  applyQueryHighlights(term) {
    const root = this.predictiveSearchResults;
    if (!root) return;
    this.stripQueryHighlights(root);
    const q = (term || '').trim();
    if (!q) return;
    const selectors = [
      '.skipp-predictive-product-title',
      '.skipp-predictive-collection-link',
      '.skipp-predictive-trending__link',
      '.skipp-predictive-product-vendor',
    ];
    selectors.forEach((sel) => {
      root.querySelectorAll(sel).forEach((el) => this.highlightTextInElement(el, q));
    });
  }

  productMatchesTerm(li, term) {
    const q = this.normalizeSearchText(term);
    if (!q) return true;

    const meta = this.getProductMetaFromLi(li);
    const title = this.normalizeSearchText(meta.title);
    if (title.includes(q)) return true; // title always wins

    const tokens = this.termTokens(q);
    const combined = `${meta.title} ${meta.type} ${meta.description} ${meta.vendor}`;
    return this.textMatchesTokens(combined, tokens);
  }

  scoreProduct(li, term) {
    const meta = this.getProductMetaFromLi(li);
    const titleScore = this.scoreTitleMatch(meta.title, term);
    const typeScore = this.scoreFieldMatch(meta.type, term, {
      exact: 420,
      startsWith: 330,
      wordStart: 280,
      contains: 220,
      tokenHit: 40,
    });
    const vendorScore = this.scoreFieldMatch(meta.vendor, term, {
      exact: 480,
      startsWith: 380,
      wordStart: 320,
      contains: 260,
      tokenHit: 45,
    });
    const descScore = this.scoreFieldMatch(meta.description, term, {
      exact: 140,
      startsWith: 110,
      wordStart: 90,
      contains: 80,
      tokenHit: 10,
    });

    return titleScore + typeScore + vendorScore + descScore;
  }

  applyClientFiltering() {
    const term = this.searchTerm;

    // Collections pills
    const collections = this.querySelectorAll('#predictive-search-results-collections-list [data-title]');
    collections.forEach((li) => {
      const title = li.getAttribute('data-title') || li.textContent || '';
      const ok = this.textMatchesTokens(title, this.termTokens(term));
      li.style.display = ok ? '' : 'none';
    });

    // Products row
    const products = this.querySelectorAll('#predictive-search-results-products-list [data-title]');
    products.forEach((li) => {
      const meta = this.getProductMetaFromLi(li);
      const ok = !this.isLuxeFromTags(meta.tags) && this.productMatchesTerm(li, term);
      li.style.display = ok ? '' : 'none';
    });
  }

  rerankCollections() {
    const list = this.querySelector('#predictive-search-results-collections-list');
    if (!list) return;

    const term = this.searchTerm;
    const items = Array.from(list.querySelectorAll(':scope > li'));
    if (items.length < 2) return;

    const scored = items.map((el, idx) => {
      const title = el.getAttribute('data-title') || el.textContent || '';
      return { el, idx, score: this.scoreTitleMatch(title, term) };
    });

    scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
    scored.forEach(({ el }) => list.appendChild(el));
  }

  rerankProducts() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const term = this.searchTerm;
    const items = Array.from(list.querySelectorAll(':scope > li'));
    if (items.length < 2) return;

    const scored = items.map((el, idx) => {
      // Keep stable ordering for equal scores via idx.
      return { el, idx, score: this.scoreProduct(el, term) };
    });

    scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
    scored.forEach(({ el }) => list.appendChild(el));
  }

  getVendorKeyFromProductLi(li) {
    const v = li?.getAttribute?.('data-vendor') || '';
    return this.normalizeVendorKey(v);
  }

  diversifyProductsByVendor({ maxItems = 18, initialCapPerVendor = 2, minGap = 4 } = {}) {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const allItems = Array.from(list.querySelectorAll(':scope > li'));
    if (allItems.length < 3) return;

    const visibleItems = allItems.filter((el) => window.getComputedStyle(el).display !== 'none');
    if (visibleItems.length < 3) return;

    const vendorOrder = [];
    const queues = new Map();
    for (const el of visibleItems) {
      const vendor = this.getVendorKeyFromProductLi(el) || '__unknown__';
      if (!queues.has(vendor)) {
        queues.set(vendor, []);
        vendorOrder.push(vendor);
      }
      queues.get(vendor).push(el);
    }

    const taken = Object.create(null);
    const picked = [];
    const lastVendors = [];

    const tryPickWithCap = (cap) => {
      for (let pass = 0; pass < 2; pass++) {
        for (const vendor of vendorOrder) {
          const q = queues.get(vendor);
          if (!q || q.length === 0) continue;
          if ((taken[vendor] || 0) >= cap) continue;
          if (pass === 0 && minGap > 0 && lastVendors.slice(-minGap).includes(vendor)) continue;

          const el = q.shift();
          taken[vendor] = (taken[vendor] || 0) + 1;
          picked.push(el);
          lastVendors.push(vendor);
          return true;
        }
      }
      return false;
    };

    const tryPickAny = () => {
      for (let pass = 0; pass < 2; pass++) {
        for (const vendor of vendorOrder) {
          const q = queues.get(vendor);
          if (!q || q.length === 0) continue;
          if (pass === 0 && minGap > 0 && lastVendors.slice(-minGap).includes(vendor)) continue;

          const el = q.shift();
          taken[vendor] = (taken[vendor] || 0) + 1;
          picked.push(el);
          lastVendors.push(vendor);
          return true;
        }
      }
      return false;
    };

    // Phase 1: 1 per vendor (maximize brand variety first)
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickWithCap(1);
      if (!progressed) break;
    }

    // Phase 2: up to 2 per vendor (your requested cap)
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickWithCap(initialCapPerVendor);
      if (!progressed) break;
    }

    // Phase 3: if still not full, allow more per vendor, but spaced out
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickAny();
      if (!progressed) break;
    }

    // Append any remaining visible items (if any) to keep list complete.
    const pickedSet = new Set(picked);
    const remainingVisible = visibleItems.filter((el) => !pickedSet.has(el));
    const finalOrder = [...picked, ...remainingVisible];

    finalOrder.forEach((el) => list.appendChild(el));
  }

  maskExclusiveVendorsInResults() {
    // Ensure vendor masking is applied even after the best-seller HTML replacement.
    const productLis = this.querySelectorAll('#predictive-search-results-products-list > li[data-vendor]');
    productLis.forEach((li) => {
      const vendorRaw = li.getAttribute('data-vendor') || '';
      if (!this.isExclusiveVendor(vendorRaw)) return;
      const vendorEl = li.querySelector('.predictive-search__item-vendor');
      if (vendorEl) vendorEl.textContent = 'EXCLUSIVE BRAND';
    });
  }

  setupProductsWheelScroll() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    // Rebind if list node changed (innerHTML replaces it).
    if (this._productsWheelBoundTo && this._productsWheelBoundTo !== list && this._productsWheelHandler) {
      this._productsWheelBoundTo.removeEventListener('wheel', this._productsWheelHandler);
      this._productsWheelBoundTo = null;
      this._productsWheelHandler = null;
    }

    if (this._productsWheelBoundTo === list && this._productsWheelHandler) return;

    const handler = (e) => {
      if (list.scrollWidth <= list.clientWidth) return;

      // Only hijack "vertical" wheel to scroll horizontally.
      const dy = e.deltaY || 0;
      const dx = e.deltaX || 0;
      if (Math.abs(dy) <= Math.abs(dx)) return;

      list.scrollLeft += dy;
      e.preventDefault();
    };

    list.addEventListener('wheel', handler, { passive: false });
    this._productsWheelBoundTo = list;
    this._productsWheelHandler = handler;
  }

  postProcessResults() {
    this.applyClientFiltering();
    this.rerankCollections();
    this.rerankProducts();
    // Diversify the first set heavily so users see many brands early.
    /* Slightly less aggressive mixing so strongest title/vendor matches stay nearer the front */
    this.diversifyProductsByVendor({ maxItems: 20, initialCapPerVendor: 2, minGap: 2 });
    this.maskExclusiveVendorsInResults();
    this.setupProductsWheelScroll();
    this.applyQueryHighlights(this.searchTerm);
  }

  getBestSellerResults(searchTerm) {
    const term = this.normalizeTerm(searchTerm);
    const key = term.replace(/\s+/g, '-');
    if (!term) return Promise.resolve(null);

    if (this.cachedBestSellerResults[key]) return Promise.resolve(this.cachedBestSellerResults[key]);

    this.bestAbortController.abort();
    this.bestAbortController = new AbortController();

    const params = new URLSearchParams({
      q: term,
      type: 'product',
      'options[prefix]': 'last',
      'options[fields]': 'title,vendor,product_type,variants.title',
      sort_by: 'best-selling',
      view: 'best-suggestions',
    });

    return fetch(`/search?${params.toString()}`, { signal: this.bestAbortController.signal })
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (!text) return null;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const root = doc.querySelector('[data-best-search-suggestions]');
        if (!root) return null;

        const pills = root.querySelector('[data-best-suggestions-pills]');
        const products = root.querySelector('[data-best-products]');

        const payload = {
          pillsHtml: pills ? pills.innerHTML : '',
          productsHtml: products ? products.innerHTML : '',
        };

        this.cachedBestSellerResults[key] = payload;
        return payload;
      })
      .catch(() => null);
  }

  async enhanceWithBestSellers() {
    // Only try on longer terms to reduce noise.
    const requested = this.normalizeTerm(this.searchTerm);
    if (requested.length < 2) return;

    const payload = await this.getBestSellerResults(requested);
    if (!payload) return;

    // Only apply if the term hasn't changed since request started.
    if (this.normalizeTerm(this.searchTerm) !== requested) return;

    const productsList = this.querySelector('#predictive-search-results-products-list');

    if (productsList && payload.productsHtml) productsList.innerHTML = payload.productsHtml;

    this.postProcessResults();
    this.adjustSkippHeaderDropdownPosition();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.renderTrendingBootstrap()) return;
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    const q = this.getQuery();
    if (!q.length) {
      if (event.code === 'Escape') this.close(true);
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Always abort the previous in-flight request so results never "lag behind" typing.
    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
    });

    // Products-only: faster, cleaner, and designed around swipeable product suggestions.
    params.set('resources[type]', 'product,collection');
    // Request a larger pool so we can diversify vendors client-side.
    params.set('resources[limit]', '80');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'hide');
    // Title-focused matching (omit body — reduces irrelevant description hits from Shopify)
    params.set('resources[options][fields]', 'title,vendor,product_type,variants.title');

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    this.postProcessResults();
    // Re-rank products by best-selling for this keyword.
    this.enhanceWithBestSellers();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // Inline header dropdown: CSS controls max-height (400px). Skip inline override.
    if (this.predictiveSearchResults.classList.contains('skipp-header-search__results')) {
      this.predictiveSearchResults.style.removeProperty('max-height');
      this.adjustSkippHeaderDropdownPosition();
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
